/**
 * The app files the literal `"unknown"` as the feature area of many automatic reports (iOS
 * 7.22.0's Oops alerts, ~15% of prod's reports in a week). That is a missing area spelled out,
 * not a part of the app, so the rankings must not list it as one (Galley #15049, the owner's
 * yes on option (c)). It keeps its own bar, still clickable to the same filter, labelled for
 * what it is and drawn after the ranked areas.
 *
 * `isDetectedFeatureArea` is the twin of the server's `feature_area_is_detected`
 * (`src/worker/lifecycle/store.rs`), which already drives the coverage-over-time chart
 * (Galley #14176) and the trend alerts (Galley #13620): NULL, blank or any-case `unknown`
 * (spaces trimmed) is not a detected area. `feature-area.test.ts` pins the same cases.
 */

/**
 * What a not-an-area value's bar reads, wherever areas are ranked: the stored value, then
 * "(no area recorded)". For the app's `"unknown"` that is "unknown (no area recorded)".
 */
export function unrecordedAreaLabel(value: string): string {
  const shown = value.replace(/^ +| +$/g, "") || "(blank)";
  return `${shown} (no area recorded)`;
}

/**
 * What a stored `feature_area` reads wherever one is printed on its own (a filter's option or
 * chip, a row's area chip, an alert's area, a lookup's Area line): a detected area as stored,
 * and a not-an-area value as `unrecordedAreaLabel` says, so "unknown" reads "unknown (no area
 * recorded)" as it does on Activity and Stories (Galley #32030). Words only: every caller still
 * filters, links, sorts and counts by the stored value.
 */
export function featureAreaLabel(raw: string): string {
  return isDetectedFeatureArea(raw) ? raw : unrecordedAreaLabel(raw);
}

/** True when `raw` names an area (the twin of the server's `feature_area_is_detected`). */
export function isDetectedFeatureArea(raw: string | null | undefined): boolean {
  if (raw == null) return false;
  const a = raw.replace(/^ +| +$/g, "");
  // ASCII-only case folding, as Rust's `eq_ignore_ascii_case`.
  const folded = a.replace(/[A-Z]/g, (c) => c.toLowerCase());
  return a !== "" && folded !== "unknown";
}

/**
 * Split one card's area counts into the areas to rank and the values the app filed without an
 * area (the literal `"unknown"`), in their input order. Nothing is dropped or merged: each
 * value keeps its own count, so a click still filters to exactly that stored value.
 */
export function splitFeatureAreas<T extends { value: string }>(
  areas: readonly T[],
): { ranked: T[]; unrecorded: T[] } {
  const ranked: T[] = [];
  const unrecorded: T[] = [];
  for (const a of areas) {
    (isDetectedFeatureArea(a.value) ? ranked : unrecorded).push(a);
  }
  return { ranked, unrecorded };
}

/**
 * True when a card's area counts include a not-an-area value with reports, so it draws the
 * "(no area recorded)" bar. Cards add the words explaining that bar only then, and otherwise
 * keep today's text (samesies, Galley #15694).
 */
export function hasUnrecordedArea(
  areas: readonly { value: string; count?: number }[],
): boolean {
  return areas.some((a) => (a.count ?? 0) > 0 && !isDetectedFeatureArea(a.value));
}

/**
 * Relabel the bands of a `feature_area`-grouped Activity series whose key is the app's literal
 * `"unknown"`. Only the label changes: the key, colour, position and values stay, and the NULL
 * band (`__null__`, "(none)") and the "Other" rollup (`__other__`) are left as they are.
 */
export function relabelUnrecordedAreaSeries<T extends { key: string; label: string }>(
  series: readonly T[],
): T[] {
  return series.map((s) =>
    s.key === "__null__" || s.key === "__other__" || isDetectedFeatureArea(s.key)
      ? s
      : { ...s, label: unrecordedAreaLabel(s.key) },
  );
}

/** What Activity's "Feature area — distribution" card calls a report with no feature area. */
export const NO_DETECTED_AREA_LABEL = "(no detected area)";

/**
 * Galley #31450: the NULL band of a `feature_area`-grouped Activity series (`__null__`, which
 * the route labels "(none)") is the same reports the distribution card counts as "(no detected
 * area)", so the over-time card calls it that too. Only the label changes: the key, colour,
 * position and values stay, and every other band is left as it is.
 */
export function labelNoDetectedAreaBand<T extends { key: string; label: string }>(
  series: readonly T[],
): T[] {
  return series.map((s) =>
    s.key === "__null__" ? { ...s, label: NO_DETECTED_AREA_LABEL } : s,
  );
}

/**
 * Galley #21226: where an area bar's click goes. The bar counts the reports
 * that arrived in the card's window (`arrived_at > generated_at - window`),
 * so the list it opens carries that window as `date_from` (the list reads
 * `arrived_at >= date_from`), not just the area: 388 in the last 24 hours
 * must open those 388, not every report the area ever had.
 */
export function areaDrillHref(
  area: string,
  generatedAt: string,
  windowHours: number,
): string {
  const href = `/bundles?feature_areas=${encodeURIComponent(area)}`;
  const end = Date.parse(generatedAt);
  if (Number.isNaN(end)) return href;
  const from = new Date(end - windowHours * 60 * 60 * 1000).toISOString();
  return `${href}&date_from=${encodeURIComponent(from)}`;
}

/**
 * A row's share of the window, as the Activity page's "By feature area" card
 * and the Users profile's ranked bars (`RankedBars`, Galley #31453) print it:
 * a whole percent, except that a share with reports that rounds to
 * 0 reads "<1%" and a share short of the whole window that rounds to 100 reads
 * ">99%" (Galley #31360: prod's 24 h showed "16 · 0%"). A zero count, or the
 * whole window, stays exact.
 */
export function sharePercent(count: number, total: number): string {
  if (total <= 0 || count <= 0) return "0%";
  const pct = Math.round((100 * count) / total);
  if (pct === 0) return "<1%";
  if (pct === 100 && count < total) return ">99%";
  return `${pct}%`;
}
